import React, { ChangeEvent } from 'react'
import { ApplicationLibraryStatus } from '../../../../graphql/schema/mutations/update-application-library-status'
import { css } from '@emotion/css'
import { PureQueryOptions } from 'apollo-client'
import {
  UpdateApplicationLibraryStatusMutation,
  UpdateApplicationLibraryStatusMutationArgs
} from '../../api/graphql/update-application-library-status-mutation'
import { I18nConsumer } from '../../../../components/i18n-provider'
import { logSuccess, logError } from '../../../../components/toaster'
import { IconBugTrackerArrow } from '@contrast-security-inc/design-system-foundations'
import { BugTracker } from '../../../../components/bug-tracker'
import {
  DeleteApplicationLibraryStatusMutation,
  DeleteApplicationLibraryStatusMutationArgs
} from '../../api/graphql/delete-application-library-status-mutation'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { TooltipContentBugTrackers } from './name-cell'
import { Tooltip } from '@contrast-security-inc/design-system-popovers'
import { Dropdown } from '@contrast-security-inc/design-system-inputs'
import { RoleSwitch } from 'contrastBase/RoleSwitch'
import { Role } from '@contrast-security-inc/base-types'

export const bugTrackerArrowTestId = 'status-cell-bug-tracker-test-id'
export class LibraryStatus {
  public static readonly BLANK: LibraryStatus = new LibraryStatus('BLANK', '')
  public static readonly NONE: LibraryStatus = new LibraryStatus(
    'NONE',
    'title_libraryStatusNone'
  )
  public static readonly REPORTED: LibraryStatus = new LibraryStatus(
    'REPORTED',
    'title_libraryStatusReported'
  )
  public static readonly REMEDIATED: LibraryStatus = new LibraryStatus(
    'REMEDIATED',
    'title_libraryStatusRemediated'
  )
  public static readonly NOT_A_PROBLEM: LibraryStatus = new LibraryStatus(
    'NOT_A_PROBLEM',
    'title_libraryStatusNotAProblem'
  )
  constructor(readonly name: string, readonly i18nID: string) {}
}

export class LibraryStatusRegistry {
  public readonly all: LibraryStatus[]

  constructor(readonly statuses: LibraryStatus[]) {
    this.all = statuses
  }
}

export const defaultLibraryStatusRegistry = new LibraryStatusRegistry([
  LibraryStatus.NONE,
  LibraryStatus.REPORTED,
  LibraryStatus.REMEDIATED,
  LibraryStatus.NOT_A_PROBLEM
])

interface StatusCellRowData {
  readonly hash: string
  readonly bugTrackers: BugTracker[]
  readonly status?: ApplicationLibraryStatus
}

type OnChangeProps = {
  intl: IntlShape
  organizationID: string
  applicationID: string
  libraryHash: string
  refresh: () => Promise<any>
  updateStatus: (
    variables: UpdateApplicationLibraryStatusMutationArgs,
    refetchQueries?: PureQueryOptions[]
  ) => Promise<any>
  deleteStatus: (
    variables: DeleteApplicationLibraryStatusMutationArgs,
    refetchQueries?: PureQueryOptions[]
  ) => Promise<any>
  refetchQueries?: PureQueryOptions[]
}

export function StatusCell({
  organizationID,
  applicationID,
  refresh,
  refetchQueries,
  rowData: { hash, status, bugTrackers },
  applicationRole
}: {
  organizationID: string
  applicationID: string
  rowData: StatusCellRowData
  refetchQueries?: PureQueryOptions[]
  refresh: () => Promise<any>
  applicationRole: Role | undefined
}) {
  return (
    <div
      className={css`
        height: 100%;
      `}
    >
      <div
        className={css`
          flex-direction: column;
          align-items: center;
        `}
      >
        <div
          className={css`
            display: flex;
            align-items: center;
            height: 34px;
            min-width: 90px;
          `}
        >
          <StatusInline
            status={status ? LibraryStatus[status] : undefined}
            organizationID={organizationID}
            applicationRole={applicationRole}
            applicationID={applicationID}
            libraryHash={hash}
            refresh={refresh}
            refetchQueries={refetchQueries}
          />
          {!!bugTrackers.length && (
            <Tooltip
              interactive
              body={
                <TooltipContentBugTrackers
                  bugTrackers={bugTrackers}
                  overrideBugtrackerListCss={css`
                    min-width: 250px;
                  `}
                />
              }
              placement="bottom"
            >
              <IconBugTrackerArrow
                testId={bugTrackerArrowTestId}
                color="neutral60"
                size="sm"
                className={css`
                  align-self: center;
                  margin-left: 5px;
                `}
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  )
}

interface StatusInlineProps {
  status?: LibraryStatus
  organizationID: string
  applicationID: string
  libraryHash: string
  refetchQueries?: PureQueryOptions[]
  refresh: () => Promise<any>
  applicationRole: Role | undefined
}

function itemToString(
  libraryStatus: LibraryStatus | undefined,
  intl: IntlShape
) {
  return libraryStatus && libraryStatus.name !== 'BLANK'
    ? intl.formatMessage({ id: libraryStatus.i18nID })
    : ''
}

const onChange =
  ({
    intl,
    organizationID,
    applicationID,
    libraryHash,
    refresh,
    updateStatus,
    deleteStatus,
    refetchQueries
  }: OnChangeProps) =>
  async (event: ChangeEvent<HTMLInputElement>) => {
    const newStatus = event.target.value as any as LibraryStatus

    if (newStatus) {
      try {
        // eslint-disable-next-line unicorn/prefer-ternary
        if (newStatus.name !== 'NONE') {
          await updateStatus(
            {
              organizationID,
              applicationID,
              hashes: [libraryHash],
              status: newStatus.name
            },
            refetchQueries
          )
        } else {
          await deleteStatus(
            {
              organizationID,
              applicationID,
              hashes: [libraryHash]
            },
            refetchQueries
          )
        }

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        refresh()
        logSuccess(
          intl.formatMessage(
            {
              id: 'detail_libraries_successfullyUpdatedLibraryStatus'
            },
            { count: 1 }
          )
        )
      } catch (error) {
        console.error(error)
        logError(
          intl.formatMessage(
            {
              id: 'detail_libraries_failedToUpdateLibraryStatus'
            },
            { count: 1 }
          )
        )
      }
    }
  }

function StatusInline({
  status,
  organizationID,
  applicationID,
  libraryHash,
  refetchQueries,
  refresh,
  applicationRole
}: StatusInlineProps) {
  const { formatMessage } = useIntl()

  return (
    <DeleteApplicationLibraryStatusMutation>
      {(deleteStatus) => {
        return (
          <UpdateApplicationLibraryStatusMutation>
            {(updateStatus) => {
              return (
                <I18nConsumer>
                  {({ intl }) => {
                    return (
                      <>
                        <RoleSwitch
                          role={applicationRole || 'VIEW'}
                          requiredRole="EDIT"
                          renderDenied={() => (
                            <div
                              className={css`
                                &:not(:hover) {
                                  background-image: none;
                                  border-color: transparent;
                                }
                              `}
                              data-testId={`libraryGridStatusCell${libraryHash}StatusCellText`}
                            >
                              <>
                                {status && (
                                  <FormattedMessage id={status.i18nID} />
                                )}
                              </>
                            </div>
                          )}
                          renderAllowed={() => (
                            <Dropdown<LibraryStatus>
                              className={css`
                                padding-top: 0;
                                /* Prevents input appearing over the table header */
                                & > div {
                                  z-index: unset;
                                }
                              `}
                              testId={`libraryGridStatusCell${libraryHash}StatusCellDropdown`}
                              itemToID={(libraryStatus) => libraryStatus.name}
                              itemToString={(libraryStatus) =>
                                itemToString(libraryStatus, intl)
                              }
                              items={defaultLibraryStatusRegistry.all}
                              value={status}
                              placeholder={formatMessage({
                                id: 'placeholder_addStatus'
                              })}
                              onChange={onChange({
                                intl,
                                organizationID,
                                applicationID,
                                libraryHash,
                                refresh,
                                updateStatus,
                                deleteStatus,
                                refetchQueries
                              })}
                            />
                          )}
                        />
                      </>
                    )
                  }}
                </I18nConsumer>
              )
            }}
          </UpdateApplicationLibraryStatusMutation>
        )
      }}
    </DeleteApplicationLibraryStatusMutation>
  )
}
